import React, { useState, useEffect, useContext, useRef } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import axios from 'axios';
import CourseProp from '../propTypes/courseProp';
import translate from '../../../commonComponents/translate';
import ModuleList from './ModuleList';
import LessonContent from './LessonContent';
import CourseNavigation from './CourseNavigation';
import Notifications from '../../../services/notifications';
import {
  compose,
  isEnrolledOrFree,
  IN_APP_PRODUCT_TYPE,
  LESSON_STATUS,
  MODULE_STATUS,
  MODULE_TYPE,
  TROPHY_CONFIG,
  throttle,
} from '../../../services/utilityFunctions';
import Request from '../../../services/request';
import withCSRF from '../../../commonComponents/withCSRF';
import CertificateDisplay from './CertificateDisplay';
import LoggedUserContext from '../../../contextProviders/LoggedUserContext';
import { useScrollAnchor } from '../../../services/customHooks';
import AppProductProp from '../../profile/propTypes/appProductProp';
import CountryProp from '../../../commonComponents/forms/Address/CountryProp';
import StripeCardProp from '../../profile/propTypes/StripeCardProp';
import PrintModal from '../../profile/mainPanel/print/PrintModal';
import GoogleAnalytics from '../../../services/googleAnalytics';
import EnrollModal from '../EnrollModal';
import CongratsModal from '../../journey/modals/CongratsModal';
import EndCourseModal from '../modal/EndCourseModal';
import CloseIcon from '../../shared/icons/CloseIcon';

const findLessonByPos = (mod, pos) =>
  mod.lessonsList.find(l => l.pos === pos) || {};

const notLocked = (isBack, currentLesson, lesson) => {
  if (lesson && lesson.status) {
    return lesson.status !== LESSON_STATUS.LOCKED;
  }
  return isBack || currentLesson.status === LESSON_STATUS.DONE;
};

const firstUnlocked = (isEnrolled, course) => {
  const orderedMods = (course.modulesList || [])
    .sort((m1, m2) => m1.pos - m2.pos)
    .filter(m => m.type === MODULE_TYPE.LESSONS);

  let firstModule = orderedMods.length > 0 ? orderedMods[0] : null;
  if (!isEnrolled) {
    if (
      firstModule &&
      firstModule.lessonsList &&
      firstModule.lessonsList.length > 0
    ) {
      return { firstModule, firstLesson: firstModule.lessonsList[0] };
    }
    return { firstModule, firstLesson: null };
  }

  const inProgress = orderedMods.filter(
    m => m.status === MODULE_STATUS.IN_PROGRESS,
  );
  if (inProgress.length > 0) {
    [firstModule] = inProgress;
  } else {
    const unlocked = orderedMods.filter(
      m => m.status === MODULE_STATUS.UNLOCKED,
    );
    if (unlocked.length > 0) {
      [firstModule] = unlocked;
    }
  }

  let firstLesson;
  if (firstModule) {
    const lessons = [...firstModule.lessonsList];
    firstLesson = lessons.length > 0 ? lessons[0] : null;

    const orderedLessons = lessons.sort((l1, l2) => l1.pos - l2.pos);
    const unlocked = orderedLessons.filter(
      l => l.status === LESSON_STATUS.UNLOCKED,
    );
    if (unlocked.length > 0) {
      [firstLesson] = unlocked;
    }
  }

  return { firstLesson, firstModule };
};

const firstCertificate = course =>
  (course.modulesList || []).find(m => m.type === MODULE_TYPE.CERTIFICATE);

const courseWithNormalizedLessonStatus = course => {
  const modulesList = (course.modulesList || []).map(mod => {
    if (mod.status === MODULE_STATUS.LOCKED) {
      const lessonsList = (mod.lessonsList || []).map(l => ({
        ...l,
        status: LESSON_STATUS.LOCKED,
      }));
      return { ...mod, lessonsList };
    }
    return mod;
  });

  return { ...course, modulesList };
};

const lessonIsUnlocked = (course, moduleId, lessonId) => {
  const modules = course.modulesList || [];
  const module = modules.find(m => m._id === moduleId);
  if (!module || module.status === MODULE_STATUS.LOCKED) {
    return false;
  }
  const lessons = module.lessonsList || [];
  const lesson = lessons.find(l => l._id === lessonId);
  return lesson && lesson.status !== LESSON_STATUS.LOCKED;
};

function CourseContentDetails(props) {
  const {
    t,
    _csrf,
    course: courseParam,
    gameId,
    onEnroll,
    onCourseChange,
    cards,
    countries,
    appProducts,
    inJourney,
    shouldScrollContent,
    openInCertificate,
    startPlaying,
    pspdfkitLicenseKey,
    isFromSession,
    sessionId,
    sessionTrainer,
    sessionContext,
    journeyContext,
    onSkipStep,
  } = props;

  const { loggedUser } = useContext(LoggedUserContext);
  const { contentElement, scrollToContent } = useScrollAnchor();

  const [course, setCourse] = useState(
    courseWithNormalizedLessonStatus(courseParam),
  );
  const isEnrolled =
    isFromSession || inJourney || isEnrolledOrFree(loggedUser, course);

  const [shouldStartPlaying, setShouldStartPlaying] = useState(
    startPlaying && isEnrolled,
  );

  const [useFullscreen, setUseFullscreen] = useState(false);

  const isSmallDisplay = useRef(false);

  /* TODO: Reimplement later
  const handleWindowResize = throttle(() => {
    isSmallDisplay.current = window.matchMedia('(max-width: 500px)').matches;
    return isSmallDisplay.current;
  }, 300);

  useEffect(() => {
    if (handleWindowResize()) {
      setUseFullscreen(shouldStartPlaying);
    }
    window.addEventListener('resize', handleWindowResize);

    return () => window.removeEventListener('resize', handleWindowResize);
  }, []);
  */

  const scrollToLesson = () => {
    scrollToContent();
    if (isSmallDisplay.current) {
      setUseFullscreen(true);
    }
  };

  const prevShouldScrollContent = useRef(shouldScrollContent);
  useEffect(() => {
    if (shouldScrollContent !== prevShouldScrollContent.current) {
      prevShouldScrollContent.current = shouldScrollContent;
      scrollToLesson();
    }
  }, [shouldScrollContent]);

  const [selectedCertificate, setSelectedCertificate] = useState(null);
  const [selectedLesson, setSelectedLesson] = useState(null);
  const [selectedModule, setSelectedModule] = useState(null);
  const [showPrintModal, setShowPrintModal] = useState(false);
  const [showEnrollModal, setShowEnrollModal] = useState(false);

  const handleScrollToContent = courseToCheck => {
    if (openInCertificate && firstCertificate(courseToCheck)) {
      setSelectedCertificate(firstCertificate(courseToCheck).certificate);
      scrollToContent();
    } else {
      const { firstLesson, firstModule } = firstUnlocked(
        isEnrolled,
        courseToCheck,
      );
      if (firstModule && firstLesson) {
        setSelectedLesson(firstLesson._id);
        setSelectedModule(firstModule._id);
        if (shouldStartPlaying) {
          scrollToLesson();
        }
      }
    }
  };
  useEffect(() => {
    handleScrollToContent(course);
  }, []);

  const handleModuleSelection = moduleId => {
    setShouldStartPlaying(false);

    const mod = course.modulesList.find(m => m._id === moduleId);
    if (
      mod &&
      mod.type === MODULE_TYPE.CERTIFICATE &&
      mod.status !== MODULE_STATUS.LOCKED
    ) {
      setSelectedLesson(null);
      setSelectedCertificate(mod.certificate || {});
      setUseFullscreen(false);
    }

    setSelectedModule(moduleId);
  };

  const [modalInfoCongrats, setModalInfoCongrats] = useState({
    show: false,
    data: null,
  });
  const showModalCongrats = mod => {
    const { imageURL, translationKey } =
      TROPHY_CONFIG[`e-learning_${mod.level}`];
    setModalInfoCongrats({
      show: true,
      data: { imageURL, name: t(translationKey) },
    });
  };
  const hideModalCongrats = () =>
    setModalInfoCongrats({ show: false, data: null });

  const [modalInfoEndCourse, setModalInfoEndCourse] = useState({ show: false });
  const showModalEndCourse = () => setModalInfoEndCourse({ show: true });
  const hideModalEndCourse = certificateId => {
    setModalInfoEndCourse({ show: false });
    if (certificateId) {
      handleModuleSelection(certificateId);
    }
  };
  // GET : Course Detail
  const getCourseDetails = () => {
    let url = `/api/academy/course/${course._id}?`;
    if (sessionContext) {
      url += `sessionContext=${sessionContext}`;
    }
    if (journeyContext) {
      url += sessionContext
        ? `&journeyContext=${journeyContext}`
        : `journeyContext=${journeyContext}`;
    }
    axios
      .get(url, {
        params: { populate: ['modules', 'survey'], session: sessionId },
        headers: {
          'Accept-Version': '1.0.0',
          'X-Gamification-API': 'Games',
        },
      })
      .then(({ data: response }) => {
        setCourse(response.data);
        if (onCourseChange) {
          onCourseChange(response.data);
        }
      })
      .catch(err => {
        Notifications.showErrorFromRequest(err, 'error_fetchCourse');
      });
  };
  let currentModule;
  let currentLesson;
  let prevLesson = {};
  let nextLesson = {};
  if (selectedLesson) {
    for (const mod of course.modulesList) {
      const tempLesson = mod.lessonsList.find(l => l._id === selectedLesson);
      if (tempLesson) {
        currentLesson = tempLesson;
        currentModule = mod;
        prevLesson = findLessonByPos(currentModule, currentLesson.pos - 1);
        nextLesson = findLessonByPos(currentModule, currentLesson.pos + 1);
        break;
      }
    }
  }

  const shouldShowNextButton = (curModule, curLesson) => {
    if (curLesson.pos >= curModule.lessonsList.length - 1) {
      const moduleToNavigate = course.modulesList.find(
        m =>
          m.pos > curModule.pos &&
          m.type !== MODULE_TYPE.INFO &&
          m.type !== MODULE_TYPE.TROPHY &&
          m.type !== MODULE_TYPE.CERTIFICATE,
      );
      return !!moduleToNavigate;
    }
    return true;
  };

  const handleNavigation = isBack => () => {
    setShouldStartPlaying(false);
    // updates the course when we transition from a lesson to another
    // this will keep the state updated so surveyContext in survey lesson
    // is always correct instead of being lost with the changes in state
    getCourseDetails();
    // Try to Navigate to next Lesson
    const pos = isBack ? currentLesson.pos - 1 : currentLesson.pos + 1;
    const toNavigate = currentModule.lessonsList.find(l => l.pos === pos);
    if (toNavigate) {
      setSelectedLesson(toNavigate._id);
      return true;
    }
    // Try to Navigate to next Module
    const posModule = isBack ? currentModule.pos - 1 : currentModule.pos + 1;
    const moduleToNavigate = course.modulesList.find(
      m =>
        (isBack ? m.pos <= posModule : m.pos >= posModule) &&
        m.type !== MODULE_TYPE.INFO &&
        m.type !== MODULE_TYPE.TROPHY &&
        m.type !== MODULE_TYPE.CERTIFICATE,
    );
    if (moduleToNavigate) {
      if (moduleToNavigate.lessonsList.length > 0) {
        setSelectedModule(moduleToNavigate._id);
        setSelectedLesson(moduleToNavigate.lessonsList[0]._id);
        return true;
      }
    }

    Notifications.showNotificationError(
      t('error'),
      t('error_academyNavigation'),
    );
    return false;
  };

  const handleLessonDone = throttle((sessionParam = undefined) => {
    if (currentLesson.status === LESSON_STATUS.UNLOCKED) {
      let url = `/academy/lesson/${currentLesson._id}/done?courseId=${course._id}`;
      if (gameId) {
        url += `&gameId=${gameId}`;
      }
      if (sessionParam) {
        // this one mainly to the trainers that are accessing the course
        url += `&session=${sessionParam}`;
      }
      if (sessionContext) {
        // this is to check the session targets instead of the course ones
        url += `&sessionContext=${sessionContext}`;
      }
      if (journeyContext) {
        url += `&journeyContext=${journeyContext}`;
      }

      new Request(_csrf)
        .put(url)
        .done(({ data }) => {
          Notifications.showNotificationSuccess(
            t('success'),
            t('academyDetails_lessonDone'),
          );

          const newCourse = courseWithNormalizedLessonStatus(data);

          const allDone = newCourse.modulesList.reduce(
            (acc, mod) => acc && mod.status === MODULE_STATUS.DONE,
            true,
          );

          if (!inJourney) {
            let firstLessonId;
            for (const module of newCourse.modulesList) {
              if (module.lessonsList) {
                for (const lesson of module.lessonsList) {
                  if (lesson._id) {
                    firstLessonId = lesson._id;
                    break;
                  }
                }
                if (firstLessonId) break;
              }
            }
            if (firstLessonId === currentLesson._id) {
              GoogleAnalytics.courseStarted(course.name);
            }
            if (allDone) {
              GoogleAnalytics.courseFinished(newCourse.name);
              showModalEndCourse();
            }
          }

          if (!allDone) {
            // check for trophies received on update
            // check on pre-update course for trophies not done
            const trophy = course.modulesList.find(mod => {
              if (
                mod.type === MODULE_TYPE.TROPHY &&
                mod.status !== MODULE_STATUS.DONE
              ) {
                const updatedMod = newCourse.modulesList.find(
                  m => m._id === mod._id,
                );
                // if it's done in the updated course, we found a trophy to display
                return updatedMod && updatedMod.status === MODULE_STATUS.DONE;
              }
              return false;
            });
            if (trophy) {
              showModalCongrats(trophy);
            }
          }

          setCourse(newCourse);
          if (onCourseChange) {
            onCourseChange(newCourse);
          }

          handleScrollToContent(newCourse);
        })
        .fail(err => {
          Notifications.showErrorFromRequest(err, 'error_lessonDone');
        });
    }
    // if the lesson is already done and its in a session (survey lesson => where is sent the sessionParam)
    // we need to update de course, so we can get the survey context
    if (
      currentLesson.status === LESSON_STATUS.DONE &&
      sessionTrainer === loggedUser._id &&
      sessionId &&
      currentLesson.type === 'survey'
    ) {
      getCourseDetails();
    }
  }, 2000);

  const handleLessonSelection = lessonId => {
    setShouldStartPlaying(false);

    if (selectedCertificate) {
      setSelectedCertificate(null);
    }

    if (lessonIsUnlocked(course, selectedModule, lessonId) || !isEnrolled) {
      setSelectedLesson(lessonId);
    }

    scrollToLesson();
  };

  useEffect(() => {
    // Clean up states, since a new course is selected from session
    // the previous lessons and states should be reset
    if (isFromSession) {
      setCourse(courseWithNormalizedLessonStatus(courseParam));
      setSelectedCertificate(null);
      setSelectedLesson(null);
      setSelectedModule(null);
      setShowPrintModal(null);
      setShowEnrollModal(null);
      setShouldStartPlaying(startPlaying && isEnrolled);
      setShouldStartPlaying(false);
      setModalInfoEndCourse({ show: false });
      setModalInfoCongrats({
        show: false,
        data: null,
      });
    }
  }, [courseParam]);
  return (
    <>
      {showPrintModal && (
        <PrintModal
          certificateId={selectedCertificate._id}
          cards={cards}
          countries={countries}
          appProduct={appProducts.find(
            p => p.productType === IN_APP_PRODUCT_TYPE.CERTIFICATE,
          )}
          product
          onClose={() => setShowPrintModal(false)}
        />
      )}
      {showEnrollModal && (
        <EnrollModal
          itemType={IN_APP_PRODUCT_TYPE.DIGITAL_CERTIFICATE}
          itemId={selectedCertificate._id}
          onClose={() => setShowEnrollModal(false)}
        />
      )}
      {modalInfoCongrats.show && (
        <CongratsModal
          trophy={modalInfoCongrats.data}
          onContinue={() => {
            hideModalCongrats();
            handleNavigation(false);
          }}
          onClose={hideModalCongrats}
          continueText="academyDetails_continueMyCourse"
        />
      )}
      {modalInfoEndCourse.show && (
        <EndCourseModal
          onClose={hideModalEndCourse}
          onCertificateSelection={handleModuleSelection}
          course={course}
        />
      )}
      <div className="course-modules">
        <ModuleList
          modules={course.modulesList}
          selectedLesson={selectedLesson}
          onLessonSelected={handleLessonSelection}
          selectedModule={selectedModule}
          onModuleSelected={handleModuleSelection}
        />
        <div
          className={cx('course-module-content', {
            fullscreen: useFullscreen,
          })}
          ref={contentElement}
        >
          {useFullscreen && (
            <div
              className="course-fullscreen-overlay"
              onClick={() => setUseFullscreen(false)}
            >
              <button
                type="button"
                className="course-fullscreen-close"
                onClick={() => setUseFullscreen(false)}
              >
                <CloseIcon title="exit fullscreen" />
              </button>
            </div>
          )}
          {selectedLesson && (
            <LessonContent
              course={course}
              gameId={gameId}
              selectedLesson={selectedLesson}
              onLessonSelected={setSelectedLesson}
              onLessonDone={handleLessonDone}
              showEnrollOverlay={!isEnrolled}
              onEnroll={onEnroll}
              startPlaying={shouldStartPlaying}
              pspdfkitLicenseKey={pspdfkitLicenseKey}
              sessionId={sessionId}
              sessionTrainer={sessionTrainer}
              onSkipStep={onSkipStep}
            />
          )}
          {selectedCertificate && (
            <CertificateDisplay
              certificate={selectedCertificate}
              course={course}
              typeCertificate="course"
              appProducts={appProducts}
              onPrint={() => setShowPrintModal(true)}
              onEmail={() => setShowEnrollModal(true)}
              sessionContext={sessionContext}
              journeyContext={journeyContext}
            />
          )}
          {selectedLesson && isEnrolled && (
            <CourseNavigation
              showPrev={currentLesson.pos > 0}
              showNext={shouldShowNextButton(currentModule, currentLesson)}
              canNavigatePrev={notLocked(true, currentLesson, prevLesson)}
              canNavigateNext={notLocked(false, currentLesson, nextLesson)}
              handleNavigationPrev={handleNavigation(true)}
              handleNavigationNext={handleNavigation(false)}
              textPrev={t('previous')}
              textNext={
                currentLesson.pos < currentModule.lessonsList.length - 1
                  ? t('next_lesson')
                  : t('next_module')
              }
            />
          )}
        </div>
      </div>
    </>
  );
}

CourseContentDetails.propTypes = {
  t: PropTypes.func.isRequired,
  _csrf: PropTypes.string.isRequired,
  course: PropTypes.shape(CourseProp.propType).isRequired,
  gameId: PropTypes.string,
  onEnroll: PropTypes.func,
  onCourseChange: PropTypes.func,
  countries: PropTypes.arrayOf(PropTypes.shape(CountryProp.propType))
    .isRequired,
  cards: PropTypes.arrayOf(PropTypes.shape(StripeCardProp.propType)).isRequired,
  inJourney: PropTypes.bool,
  shouldScrollContent: PropTypes.bool,
  openInCertificate: PropTypes.bool,
  startPlaying: PropTypes.bool,
  appProducts: PropTypes.arrayOf(PropTypes.shape(AppProductProp.propType))
    .isRequired,
  pspdfkitLicenseKey: PropTypes.string,
  isFromSession: PropTypes.bool,
  sessionId: PropTypes.string,
  sessionTrainer: PropTypes.string,
  sessionContext: PropTypes.string,
  journeyContext: PropTypes.string,
  onSkipStep: PropTypes.func,
};

CourseContentDetails.defaultProps = {
  gameId: null,
  onCourseChange: null,
  onEnroll: null,
  inJourney: false,
  shouldScrollContent: false,
  openInCertificate: false,
  startPlaying: false,
  pspdfkitLicenseKey: null,
  isFromSession: false,
  sessionId: null,
  sessionTrainer: null,
  sessionContext: null,
  journeyContext: null,
  onSkipStep: null,
};

export default compose(translate, withCSRF)(CourseContentDetails);
